import { AlarmGroupWrapper } from '@hakimo-ui/hakimo/feature-shared';
import { AlarmDetailProvider } from '@hakimo-ui/hakimo/providers';
import { WsConnectionStatus } from '@hakimo-ui/hakimo/types';
import {
  useAlarmDetailWs,
  useAuthUtils,
  withAuthz,
  withErrorBoundary,
} from '@hakimo-ui/hakimo/util';
import { Alert, HakimoSpinner } from '@hakimo-ui/shared/ui-base';
import { ReactNode, useMemo } from 'react';
import { Route, Routes } from 'react-router-dom';

function MonitoringRootComponent() {
  const { getAccessToken } = useAuthUtils();

  const wsUrl = useMemo(() => {
    const host = window.location.hostname;
    if (host === 'localhost') {
      const port = window.location.port;
      return `ws://${host}:${port}/locationalarms`;
    }
    return `wss://location-alarm-flow-${host}/locationalarms`;
  }, []);

  return (
    <Routes>
      <Route
        path="/*"
        element={
          <AlarmDetailProvider url={wsUrl} getAccessToken={getAccessToken}>
            <WsHandler>
              <AlarmGroupWrapper />
            </WsHandler>
          </AlarmDetailProvider>
        }
      />
    </Routes>
  );
}

export const MonitoringRoot = withAuthz(
  withErrorBoundary(MonitoringRootComponent),
  ['location_alarms/monitoring:count']
);

interface WsHandlerProps {
  children: ReactNode;
}

function WsHandler(props: WsHandlerProps) {
  const {
    connectionState: { status, nextRetryIn },
  } = useAlarmDetailWs();
  switch (status) {
    case WsConnectionStatus.PENDING:
      return (
        <div className="z-10 flex h-full w-full items-center justify-center">
          <HakimoSpinner />
        </div>
      );
    case WsConnectionStatus.CLOSED:
      return (
        <Alert type="error">
          Connection terminated. This may be due to a connection error or login
          from another location. Refresh to use Monitoring in this window.
        </Alert>
      );
    case WsConnectionStatus.ERROR:
      return (
        <Alert type="error">
          An error occurred while connecting to monitoring. Please try again
          later.
        </Alert>
      );
    case WsConnectionStatus.RECONNECTING:
    case WsConnectionStatus.CONNECTED:
      return (
        <>
          {status === WsConnectionStatus.RECONNECTING && (
            <Alert type="info">
              Unable to connect. Trying again in {nextRetryIn} seconds... Make
              sure monitoring is not open in another tab
            </Alert>
          )}
          {props.children}
        </>
      );
      return;

    default:
      return null;
  }
}
